import type { ComponentProps, FC } from "react";

import React from "react";

import { Button } from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { PanelLeftOpen, PanelRightOpen } from "lucide-react";

interface HeaderProps extends ComponentProps<"div"> {
  position: "left" | "right";
}
function Header({ ref, children, className, position, ...props }: HeaderProps) {
  const { collapse } = useExpandablePanelContext();
  return (
    <h2
      className={cn(
        "af-flex af-items-center af-gap-2 af-text-base af-font-semibold af-text-foreground",
        className,
      )}
      ref={ref}
      {...props}
    >
      <span className="af-flex af-w-full af-items-center af-justify-between">
        <span className="af-flex af-items-center af-gap-2">{children}</span>
        <Button
          size="icon"
          variant="ghost"
          onClick={collapse}
          aria-label="Collapse files list"
          data-testid="documents-pane-toggle"
          className="af-text-muted-foreground"
        >
          {position === "left" && <PanelRightOpen className="af-size-4" />}
          {position === "right" && <PanelLeftOpen className="af-size-4" />}
        </Button>
      </span>
    </h2>
  );
}

type BodyProps = ComponentProps<"div">;
function Body({ ref, ...props }: BodyProps) {
  const { children, className, ...restProps } = props;
  return (
    <div
      className={cn(
        "af-flex af-min-h-0 af-flex-1 af-flex-col af-justify-between af-gap-4",
        className,
      )}
      ref={ref}
      {...restProps}
    >
      {children}
    </div>
  );
}

interface ExpandablePanelContextValue {
  collapse: () => void;
  expand: () => void;
}
const ExpandablePanelContext =
  React.createContext<ExpandablePanelContextValue | null>(null);

function useExpandablePanelContext() {
  const context = React.useContext(ExpandablePanelContext);
  if (!context) {
    throw new Error(
      "useExpandablePanelContext must be used within a ExpandablePanel component",
    );
  }
  return context;
}

interface ExpandablePanelProps extends ComponentProps<"div"> {
  isExpanded: boolean;
  collapse: () => void;
  expand: () => void;
  expandedContent?: React.ReactNode;
  collapsedContent?: React.ReactNode;
}
function ExpandablePanelBase({ ref, ...props }: ExpandablePanelProps) {
  const {
    isExpanded,
    collapse,
    expand,
    className,
    expandedContent,
    collapsedContent,
    ...restProps
  } = props;
  return (
    <ExpandablePanelContext value={{ collapse, expand }}>
      <div
        className={cn(
          `af-h-full af-border-border af-bg-interface-background`,
          {
            "af-pb-6": isExpanded,
          },
          className,
        )}
        ref={ref}
        {...restProps}
      >
        {isExpanded ? (
          <div className={cn("af-flex af-h-full af-flex-col")}>
            {expandedContent}
          </div>
        ) : (
          collapsedContent
        )}
      </div>
    </ExpandablePanelContext>
  );
}

interface CollapsedContentsProps extends React.PropsWithChildren {
  position: "left" | "right";
  actions: React.ReactNode;
  expandLabel: string;
  /**
   * Optional className merged into the toggle Button. Use to override
   * defaults like `af-h-12` so the collapsed-strip button can match a
   * custom expanded header height.
   */
  buttonClassName?: string;
}
const CollapsedContents: FC<CollapsedContentsProps> = ({
  position,
  actions,
  children,
  expandLabel,
  buttonClassName,
}) => {
  const { expand } = useExpandablePanelContext();

  return (
    <div className="af-flex af-h-full af-flex-col af-justify-between">
      <div className="af-min-w-12">
        <Button
          size="icon"
          variant="ghost"
          className={cn(
            "af-h-12 af-w-full af-rounded-none af-text-muted-foreground",
            !children && "af-border-b af-border-border",
            buttonClassName,
          )}
          onClick={expand}
          aria-label={expandLabel}
          data-testid="documents-pane-toggle"
        >
          {position === "right" && <PanelRightOpen className="af-size-4" />}
          {position === "left" && <PanelLeftOpen className="af-size-4" />}
        </Button>
        {children && (
          <div className="af-relative af-grid af-items-center af-justify-center af-border-t af-border-border af-py-2">
            {children}
          </div>
        )}
      </div>
      <div className="af-px-2 af-pb-6">{actions}</div>
    </div>
  );
};

export const ExpandablePanel = Object.assign(ExpandablePanelBase, {
  Header,
  Body,
  CollapsedContents,
});
